// src/pages/HirerVerification.js
// Branch 4: Admin can manually grant/revoke Fast Pay badge per hirer
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Paginator from '../components/Paginator';

const PAGE_SIZE = 15;

// ─── Province registry links ────────────────────────────────────────────────
const PROVINCE_REGISTRIES = {
  federal: { label: '🍁 Federal — Corporations Canada', url: 'https://ised-isde.canada.ca/cc/lgcy/fdrlCrpSrch.html' },
  AB: { label: '🏔️ Alberta Registry', url: 'https://www.alberta.ca/corporate-registry-search' },
  BC: { label: '🌲 BC Registry', url: 'https://www.bcregistry.gov.bc.ca' },
  MB: { label: 'Manitoba Companies', url: 'https://www.Manitoba.ca/companies/search' },
  NB: { label: 'New Brunswick Registry', url: 'https://www.snb.ca/e/1000/1000-e.asp' },
  NL: { label: 'NL Registry of Deeds', url: 'https://www.gs.gov.nl.ca/registries/deeds/' },
  NS: { label: 'Nova Scotia Registry', url: 'https://beta.novascotia.ca/programs-and-services/registry-joint-stock-companies' },
  NT: { label: 'NT Corporate Registry', url: 'https://www.ntaccess.gov.nt.ca/forms/corporate/' },
  NU: { label: 'Nunavut Registry', url: 'https://www.gov.nu.ca/justice/information/corporate-registries' },
  ON: { label: '🏙️ Ontario Business Registry', url: 'https://www.ontario.ca/page/ontario-business-registry' },
  PE: { label: 'PEI Registry', url: 'https://www.princeedwardisland.ca/en/service/search-pei-corporations' },
  QC: { label: '🔵 Québec REQ', url: 'https://www.registreentreprises.gouv.qc.ca/en/default.aspx' },
  SK: { label: 'Saskatchewan Corporations', url: 'https://www.isc.ca/BusinessRegistry/Pages/default.aspx' },
  YT: { label: 'Yukon Corporate Registry', url: 'https://yukon.ca/en/doing-business/register-business/search-yukon-business' },
};

const Badge = ({ status }) => {
  const map = {
    pending:       { bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.3)',  text: '#EAB308', label: '⏳ Pending' },
    verified:      { bg: 'rgba(34,197,94,0.1)',  border: 'rgba(34,197,94,0.3)',  text: '#22C55E', label: '✅ Verified' },
    rejected:      { bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.3)',  text: '#EF4444', label: '❌ Rejected' },
    not_submitted: { bg: 'rgba(85,85,85,0.1)',   border: 'rgba(85,85,85,0.3)',   text: 'var(--text-muted)',    label: '📄 No business docs submitted' },
  };
  const c = map[status] || map.not_submitted;
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
      {c.label}
    </span>
  );
};

const s = {
  page: { color: 'var(--text)' },
  header: { marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { color: 'var(--text-muted)', fontSize: 14, marginTop: 6 },

  // Registry toolbar
  registryBar: { background: 'var(--bg)', border: '1px solid var(--surface2)', borderRadius: 12, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  registryLabel: { color: 'var(--text-secondary)', fontSize: 13, flexShrink: 0 },
  registryBtn: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },

  filters: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  filterBtn: (active) => ({ background: active ? '#F97316' : 'var(--surface)', border: `1px solid ${active ? '#F97316' : 'var(--border)'}`, color: active ? '#fff' : 'var(--text-secondary)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }),

  card: { background: 'var(--bg)', border: '1px solid var(--surface2)', borderRadius: 14, padding: 20, marginBottom: 12 },
  cardPending: { background: 'var(--bg)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 14, padding: 20, marginBottom: 12 },

  hirerTop: { display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  avatar: { width: 48, height: 48, borderRadius: '50%', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },

  infoBlock: { flex: 1, minWidth: 200 },
  companyName: { color: 'var(--text)', fontWeight: 700, fontSize: 16, marginBottom: 6 },
  infoRow: { color: 'var(--text-secondary)', fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 4 },

  businessInfo: { background: 'var(--bg)', border: '1px solid var(--surface2)', borderRadius: 8, padding: '10px 14px', marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 },
  bnLabel: { color: 'var(--text-muted)', fontSize: 11, marginBottom: 2 },
  bnValue: { color: 'var(--text)', fontWeight: 600, fontFamily: 'monospace', fontSize: 14 },
  typeLabel: { color: 'var(--text-muted)', fontSize: 11, marginBottom: 2 },
  typeValue: { color: 'var(--text)', fontSize: 13 },

  verifyBtn: (reg) => ({
    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
    color: '#3B82F6', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
  }),

  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0, alignItems: 'flex-start' },
  approveBtn: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  rejectBtn:  { background: 'rgba(239,68,68,0.1)',  border: '1px solid rgba(239,68,68,0.3)',  color: '#EF4444', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  suspendBtn: { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  reactivateBtn: { background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },

  // Fast Pay badge admin controls
  fastPayGrantBtn:  { background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)', color: '#3B82F6', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  fastPayRevokeBtn: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  fastPayStats: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, padding: '8px 12px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 8, flexWrap: 'wrap' },
  fastPayStatLabel: { color: 'var(--text-muted)', fontSize: 11 },
  fastPayStatValue: { color: '#3B82F6', fontWeight: 700, fontSize: 13 },
  fastPayBadgeActive: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', color: '#3B82F6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  fastPayManualNote: { color: '#F97316', fontSize: 11, fontStyle: 'italic' },

  // Fast Pay modal
  fpModalTitle:    { color: 'var(--text)', fontSize: 17, fontWeight: 700, margin: '0 0 4px' },
  fpModalSub:      { color: 'var(--text-muted)', fontSize: 13, margin: '0 0 20px' },
  fpSection:       { background: 'var(--bg)', border: '1px solid var(--surface2)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 },
  fpSectionTitle:  { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  fpRow:           { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 },
  fpRowLast:       { display: 'flex', alignItems: 'flex-start', gap: 10 },
  fpDot:           (color) => ({ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 5 }),
  fpRowText:       { color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.55, flex: 1 },
  fpRowStrong:     { color: 'var(--text)', fontWeight: 600 },
  fpWarningBox:    { background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 10 },
  fpWarningText:   { color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.55, flex: 1 },
  fpBlockedBox:    { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 10 },
  fpBlockedText:   { color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.55, flex: 1 },
  fpConfirmBtn:    (color) => ({ flex: 1, background: color, border: 'none', borderRadius: 8, padding: '10px 0', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }),

  docSection: { marginTop: 14, borderTop: '1px solid var(--surface)', paddingTop: 14 },
  docImg: { width: '100%', maxWidth: 500, borderRadius: 8, border: '1px solid var(--border)', marginTop: 8, cursor: 'pointer' },

  rejectionBox: { background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12, color: '#EF4444' },
  attemptsTag: { background: 'var(--surface2)', borderRadius: 20, padding: '2px 10px', fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 },

  emptyState: { textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'var(--bg)', borderRadius: 16, border: '1px solid var(--surface2)' },

  // Reject modal
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 440 },
  modalTitle: { color: 'var(--text)', fontSize: 18, fontWeight: 600, margin: '0 0 8px' },
  modalSub: { color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20, lineHeight: 1.6 },
  textarea: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14, minHeight: 80, resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  modalBtns: { display: 'flex', gap: 10, marginTop: 16 },
  cancelBtn: { flex: 1, background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: 10, color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' },
  confirmRejectBtn: (ready) => ({ flex: 1, background: ready ? '#EF4444' : 'var(--border)', border: 'none', borderRadius: 8, padding: 10, color: ready ? '#fff' : 'var(--text-muted)', fontSize: 14, fontWeight: 600, cursor: ready ? 'pointer' : 'not-allowed' }),
};

export default function HirerVerification() {
  const [hirers, setHirers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [fastPayModal, setFastPayModal] = useState(null); // { hirer, action: 'grant'|'revoke'|'blocked', blockedReason }
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === 'all'
        ? query(collection(db, 'users'), where('role', '==', 'hirer'))
        : query(collection(db, 'users'), where('role', '==', 'hirer'), where('verificationStatus', '==', filter));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort: pending first
      list.sort((a, b) => {
        if (a.verificationStatus === 'pending' && b.verificationStatus !== 'pending') return -1;
        if (b.verificationStatus === 'pending' && a.verificationStatus !== 'pending') return 1;
        return 0;
      });
      setHirers(list);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => { setPage(1); }, [filter, search]);

  const handleApprove = async (hirer) => {
    setProcessing(hirer.id);
    try {
      await updateDoc(doc(db, 'users', hirer.id), {
        verificationStatus: 'verified',
        verificationReviewedAt: serverTimestamp(),
        verificationRejectionReason: null,
      });
      load();
    } catch (err) { alert('Error approving hirer'); }
    finally { setProcessing(null); }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim() || !rejectTarget) return;
    setProcessing(rejectTarget.id);
    try {
      await updateDoc(doc(db, 'users', rejectTarget.id), {
        verificationStatus: 'rejected',
        verificationRejectionReason: rejectReason.trim(),
        verificationReviewedAt: serverTimestamp(),
      });
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) { alert('Error rejecting hirer'); }
    finally { setProcessing(null); }
  };

  const handleSuspend = async (hirer) => {
    if (!window.confirm(`Suspend ${hirer.companyName || hirer.fullName}?`)) return;
    await updateDoc(doc(db, 'users', hirer.id), { accountStatus: 'suspended' });
    load();
  };

  const handleReactivate = async (hirer) => {
    await updateDoc(doc(db, 'users', hirer.id), { accountStatus: 'active' });
    load();
  };

  // ── Delete ghost account (no doc, unverified email) ───────────────────────
  const handleDelete = async (hirer) => {
    const name = hirer.companyName || hirer.fullName;
    const typed = window.prompt(
      `⚠️ Delete account for ${name}?\n\n` +
      `This hirer never submitted a business document and likely has an invalid email.\n\n` +
      `Type "${name}" to confirm permanent deletion. This cannot be undone.`
    );
    if (typed !== name) {
      if (typed !== null) alert('Name did not match. Deletion cancelled.');
      return;
    }
    setProcessing(hirer.id);
    try {
      await updateDoc(doc(db, 'users', hirer.id), {
        accountStatus: 'pending_deletion',
        deletedByAdmin: true,
        deletionRequestedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'dataRequests'), {
        userId:      hirer.id,
        userEmail:   hirer.email,
        type:        'deletion',
        status:      'completed',
        requestedBy: 'admin',
        requestedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
        completedBy: 'admin',
      });
      load();
      alert(`🗑️ Deletion initiated for ${name}. Cloud Function is purging all data now.`);
    } catch (err) {
      alert('Error deleting account. Please try again.');
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  // ── Branch 4: Manual Fast Pay badge grant / revoke ────────────────────────
  const handleToggleFastPay = (hirer) => {
    const isCurrentlyGranted = hirer.fastPayBadge === true;

    if (!isCurrentlyGranted) {
      // Check if blocked
      const avg = hirer.paymentAvgRating;
      if (avg != null && avg < 3.5) {
        setFastPayModal({ hirer, action: 'blocked', blockedReason: avg });
        return;
      }
      setFastPayModal({ hirer, action: 'grant' });
    } else {
      setFastPayModal({ hirer, action: 'revoke' });
    }
  };

  const handleFastPayConfirm = async () => {
    if (!fastPayModal || fastPayModal.action === 'blocked') return;
    const { hirer, action } = fastPayModal;
    const granting = action === 'grant';

    setProcessing(hirer.id + '_fastpay');
    setFastPayModal(null);
    try {
      await updateDoc(doc(db, 'users', hirer.id), {
        fastPayBadge:          granting,
        fastPayManualOverride: granting,
        fastPayOverrideBy:     'admin',
        fastPayOverrideAt:     serverTimestamp(),
      });
      load();
    } catch (err) {
      alert('Error updating Fast Pay badge');
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  const getRegistryInfo = (hirer) => {
    if (hirer.registrationType === 'federal') return PROVINCE_REGISTRIES.federal;
    if (hirer.registrationType === 'provincial' && hirer.province) return PROVINCE_REGISTRIES[hirer.province];
    return null;
  };

  const pendingCount = hirers.filter(h => h.verificationStatus === 'pending').length;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>🏢 Hirer Verification</h1>
        <p style={s.subtitle}>Review business documents and verify hirers posting jobs on Crewboard</p>
      </div>

      {/* Registry Lookup Toolbar */}
      <div style={s.registryBar}>
        <span style={s.registryLabel}>🔍 Look up business registry:</span>
        <a href={PROVINCE_REGISTRIES.federal.url} target="_blank" rel="noopener noreferrer" style={{ ...s.registryBtn, color: '#F97316', borderColor: 'rgba(249,115,22,0.3)', background: 'rgba(249,115,22,0.06)' }}>
          🍁 Corporations Canada (Federal)
        </a>
        <a href={PROVINCE_REGISTRIES.AB.url} target="_blank" rel="noopener noreferrer" style={s.registryBtn}>🏔️ Alberta</a>
        <a href={PROVINCE_REGISTRIES.BC.url} target="_blank" rel="noopener noreferrer" style={s.registryBtn}>🌲 BC</a>
        <a href={PROVINCE_REGISTRIES.ON.url} target="_blank" rel="noopener noreferrer" style={s.registryBtn}>🏙️ Ontario</a>
        <a href={PROVINCE_REGISTRIES.QC.url} target="_blank" rel="noopener noreferrer" style={s.registryBtn}>🔵 Québec</a>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by company name, contact name, email, phone or business number..."
          style={{
            flex: 1, background: 'var(--bg)', border: '1px solid var(--surface2)',
            borderRadius: 12, padding: '10px 16px', color: 'var(--text)',
            fontSize: 13, outline: 'none',
          }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}
          >✕</button>
        )}
      </div>

      {/* Filters */}
      <div style={s.filters}>
        {[
          { id: 'pending', label: `⏳ Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { id: 'verified', label: '✅ Verified' },
          { id: 'rejected', label: '❌ Rejected' },
          { id: 'all', label: 'All Hirers' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={s.filterBtn(filter === f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={s.emptyState}>Loading hirers...</div>
      ) : (() => {
        const q = search.trim().toLowerCase();
        const displayed = q ? hirers.filter(h =>
          h.companyName?.toLowerCase().includes(q) ||
          h.fullName?.toLowerCase().includes(q) ||
          h.email?.toLowerCase().includes(q) ||
          h.phone?.includes(q) ||
          h.businessNumber?.toLowerCase().includes(q)
        ) : hirers;

        const isEmpty = displayed.length === 0;
        const totalPages = Math.ceil(displayed.length / PAGE_SIZE);
        const paginated = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

        return (
          <div>
            {/* Results count */}
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 16 }}>
              {search
                ? `${displayed.length} result${displayed.length !== 1 ? 's' : ''} for "${search}" · ${hirers.length} total in "${filter}"`
                : `${hirers.length} hirer${hirers.length !== 1 ? 's' : ''} · ${filter} filter`}
            </div>

            {isEmpty ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div>{search ? `No hirers match "${search}"` : 'No hirers found for this filter'}</div>
                {search && <button onClick={() => setSearch('')} style={{ marginTop: 12, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Clear search</button>}
              </div>
            ) : (
            <div>
            {paginated.map(hirer => {
          const registry = getRegistryInfo(hirer);
          const isPending = hirer.verificationStatus === 'pending';
          const attempts = hirer.verificationAttempts || 0;

          return (
            <div key={hirer.id} style={isPending ? s.cardPending : s.card}>
              <div style={s.hirerTop}>
                <div style={s.avatar}>🏢</div>

                <div style={s.infoBlock}>
                  {/* Company + badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={s.companyName}>{hirer.companyName || hirer.fullName}</span>
                    <Badge status={hirer.verificationStatus || 'not_submitted'} />
                    {hirer.accountStatus === 'suspended' && (
                      <span style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>⛔ Suspended</span>
                    )}
                    {attempts > 0 && (
                      <span style={s.attemptsTag}>Attempt {attempts}/3</span>
                    )}
                  </div>

                  {/* Contact info */}
                  <div style={s.infoRow}>
                    <span>👤 {hirer.fullName}</span>
                    <span>📧 {hirer.email}</span>
                    <span>📱 {hirer.phone || 'N/A'}</span>
                  </div>

                  {/* Business info block */}
                  {hirer.businessNumber && (
                    <div style={s.businessInfo}>
                      <div>
                        <div style={s.bnLabel}>BUSINESS NUMBER</div>
                        <div style={s.bnValue}>{hirer.businessNumber}</div>
                      </div>
                      <div>
                        <div style={s.typeLabel}>REGISTRATION TYPE</div>
                        <div style={s.typeValue}>
                          {hirer.registrationType === 'federal' ? '🍁 Federal' : `🏛️ Provincial${hirer.province ? ` — ${hirer.province}` : ''}`}
                        </div>
                      </div>
                      {registry && (
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <a href={registry.url} target="_blank" rel="noopener noreferrer" style={s.verifyBtn()}>
                            🔍 Verify on {registry.label}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rejection reason */}
                  {hirer.verificationRejectionReason && (
                    <div style={s.rejectionBox}>
                      <strong>Rejection reason:</strong> {hirer.verificationRejectionReason}
                    </div>
                  )}

                  {/* Locked out message */}
                  {attempts >= 3 && hirer.verificationStatus === 'rejected' && (
                    <div style={{ ...s.rejectionBox, borderColor: 'rgba(234,179,8,0.3)', color: '#EAB308', background: 'rgba(234,179,8,0.06)', marginTop: 8 }}>
                      ⚠️ Max attempts reached — hirer must contact support to proceed
                    </div>
                  )}

                  {/* ── Branch 4: Fast Pay stats + badge status ── */}
                  <div style={s.fastPayStats}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {hirer.fastPayBadge
                        ? <span style={s.fastPayBadgeActive}>⚡ Fast Pay Active</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>⚡ Fast Pay — not earned</span>
                      }
                      {hirer.fastPayManualOverride && (
                        <span style={s.fastPayManualNote}>(manually set by admin)</span>
                      )}
                    </div>
                    {hirer.paymentAvgRating != null && (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        Payment avg: <span style={s.fastPayStatValue}>{hirer.paymentAvgRating}★</span>
                      </span>
                    )}
                    {hirer.paymentReviewCount != null && (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        Payment reviews: <span style={s.fastPayStatValue}>{hirer.paymentReviewCount}</span>
                      </span>
                    )}
                    {(hirer.paymentAvgRating == null && hirer.paymentReviewCount == null) && (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No payment reviews yet</span>
                    )}
                  </div>
                </div>

                {/* Action buttons — only shown when accountStatus is not unverified.
                    If no doc, not showing approve and reject buttons. Delete is handled in User Management. */}
                {hirer.accountStatus !=='unverified' && (
                <div style={s.actions}>
                  {hirer.verificationStatus !== 'verified' && hirer.verificationDocUrl &&(
                    <button onClick={() => handleApprove(hirer)} disabled={processing === hirer.id} style={s.approveBtn}>
                      ✅ Approve
                    </button>
                  )}
                  {hirer.verificationStatus !== 'rejected' && hirer.verificationDocUrl &&(
                    <button onClick={() => setRejectTarget(hirer)} style={s.rejectBtn}>
                      ❌ Reject
                    </button>
                  )}
                  {hirer.accountStatus !== 'suspended' ? (
                    <button onClick={() => handleSuspend(hirer)} style={s.suspendBtn}>⛔ Suspend</button>
                  ) : (
                    <button onClick={() => handleReactivate(hirer)} style={s.reactivateBtn}>🔓 Reactivate</button>
                  )}
                  {/* ── Branch 4: Fast Pay toggle ── */}
                  {(hirer.fastPayBadge
                    ? (
                      <button
                        onClick={() => handleToggleFastPay(hirer)}
                        disabled={processing === hirer.id + '_fastpay'}
                        style={s.fastPayRevokeBtn}
                        title="Remove the Fast Pay badge from this hirer's profile"
                      >
                        ⚡ Revoke Fast Pay
                      </button>
                    ) : (() => {
                        const blocked = hirer.paymentAvgRating != null && hirer.paymentAvgRating < 3.5;
                        return (
                          <button
                            onClick={() => handleToggleFastPay(hirer)}
                            disabled={processing === hirer.id + '_fastpay'}
                            style={blocked
                              ? { ...s.fastPayGrantBtn, opacity: 0.35, cursor: 'not-allowed', filter: 'grayscale(1)' }
                              : s.fastPayGrantBtn
                            }
                            title={blocked
                              ? `Cannot grant — payment avg is ${hirer.paymentAvgRating}★ (minimum 3.5★)`
                              : 'Manually grant the Fast Pay badge'
                            }
                          >
                            ⚡ Grant Fast Pay
                            {blocked && <span style={{ marginLeft: 5, fontSize: 10 }}>({hirer.paymentAvgRating}★)</span>}
                          </button>
                        );
                      })()
                  )}
                </div>
                )}
              </div>

              {/* Document viewer */}
              {hirer.verificationDocUrl && (
                <div style={s.docSection}>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>📄 SUBMITTED DOCUMENT</div>
                  {expandedDoc === hirer.id ? (
                    <>
                      <img
                        src={hirer.verificationDocUrl}
                        alt="Business document"
                        style={s.docImg}
                        onClick={() => setExpandedDoc(null)}
                      />
                      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>Click image to collapse · <a href={hirer.verificationDocUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6' }}>Open full size ↗</a></div>
                    </>
                  ) : (
                    <button
                      onClick={() => setExpandedDoc(hirer.id)}
                      style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}
                    >
                      👁️ View Document
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
            {displayed.length > PAGE_SIZE && (
              <Paginator page={page} totalPages={totalPages} totalItems={displayed.length} pageSize={PAGE_SIZE} onChange={setPage} />
            )}
            </div>
            )}
          </div>
        );
      })()}

      {/* Reject Modal */}
      {rejectTarget && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <h3 style={s.modalTitle}>❌ Reject Verification</h3>
            <p style={s.modalSub}>
              Rejecting <strong style={{ color: 'var(--text)' }}>{rejectTarget.companyName || rejectTarget.fullName}</strong>.
              {(rejectTarget.verificationAttempts || 0) >= 2
                ? ' ⚠️ This is their final attempt — they will be locked out and must contact support.'
                : ` They will have ${3 - (rejectTarget.verificationAttempts || 0) - 1} attempt(s) remaining.`
              }
            </p>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }}>Reason for rejection (shown to hirer) *</label>
            <textarea
              style={s.textarea}
              placeholder="e.g. Document was blurry, business number not found in registry, wrong document type..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
            <div style={s.modalBtns}>
              <button style={s.cancelBtn} onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancel</button>
              <button
                style={s.confirmRejectBtn(rejectReason.trim().length > 5)}
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || processing === rejectTarget?.id}
              >
                {processing === rejectTarget?.id ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Fast Pay Badge Modal ── */}
      {fastPayModal && (() => {
        const { hirer, action, blockedReason } = fastPayModal;
        const name = hirer.companyName || hirer.fullName;
        const avg  = hirer.paymentAvgRating;
        const reviewCount = hirer.paymentReviewCount || 0;

        return (
          <div style={s.modal}>
            <div style={{ ...s.modalBox, maxWidth: 480 }}>

              {/* ── BLOCKED ── */}
              {action === 'blocked' && <>
                <h3 style={s.fpModalTitle}>⚡ Cannot Grant Fast Pay Badge</h3>
                <p style={s.fpModalSub}>{name}</p>

                <div style={s.fpBlockedBox}>
                  <span>🚫</span>
                  <span style={s.fpBlockedText}>
                    This hirer has a payment rating of <strong style={{ color: '#EF4444' }}>{blockedReason}★</strong> — below the minimum of <strong style={{ color: 'var(--text)' }}>3.5★</strong> required for manual grants. Granting the badge to a low-rated hirer would mislead drivers.
                  </span>
                </div>

                <div style={s.fpSection}>
                  <div style={s.fpSectionTitle}>✅ Conditions to Grant Fast Pay</div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}><strong style={s.fpRowStrong}>Automatic:</strong> Payment avg ≥ 4.5★ across 3+ reviews — badge sets itself</span>
                  </div>
                  <div style={s.fpRowLast}>
                    <div style={s.fpDot('#3B82F6')} />
                    <span style={s.fpRowText}><strong style={s.fpRowStrong}>Manual (admin):</strong> Payment avg ≥ 3.5★ OR no payment data yet — for new hirers you've personally verified</span>
                  </div>
                </div>

                <div style={s.modalBtns}>
                  <button style={s.cancelBtn} onClick={() => setFastPayModal(null)}>Close</button>
                </div>
              </>}

              {/* ── GRANT ── */}
              {action === 'grant' && <>
                <h3 style={s.fpModalTitle}>⚡ Grant Fast Pay Badge</h3>
                <p style={s.fpModalSub}>{name}</p>

                {avg != null && avg < 4.5 && (
                  <div style={s.fpWarningBox}>
                    <span>⚠️</span>
                    <span style={s.fpWarningText}>
                      This hirer's payment avg is <strong style={{ color: '#EAB308' }}>{avg}★</strong> — below the automatic threshold of 4.5★. You're overriding the system. Make sure you have a good reason.
                    </span>
                  </div>
                )}

                <div style={s.fpSection}>
                  <div style={s.fpSectionTitle}>✅ When you CAN grant</div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>Payment avg is <strong style={s.fpRowStrong}>≥ 3.5★</strong> {avg != null ? `(current: ${avg}★ ✓)` : ''}</span>
                  </div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>New hirer with <strong style={s.fpRowStrong}>no payment data yet</strong> — you've personally confirmed they pay quickly</span>
                  </div>
                  <div style={s.fpRowLast}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>Hirer has fewer than <strong style={s.fpRowStrong}>3 payment reviews</strong> but strong overall track record {reviewCount > 0 ? `(${reviewCount} review${reviewCount !== 1 ? 's' : ''} so far)` : ''}</span>
                  </div>
                </div>

                <div style={s.fpSection}>
                  <div style={s.fpSectionTitle}>⚠️ What happens after granting</div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#F97316')} />
                    <span style={s.fpRowText}><strong style={s.fpRowStrong}>fastPayManualOverride</strong> is set — the Cloud Function will not auto-revoke this badge when new reviews arrive</span>
                  </div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#F97316')} />
                    <span style={s.fpRowText}>Badge appears immediately on all job cards and the hirer's profile for all drivers</span>
                  </div>
                  <div style={s.fpRowLast}>
                    <div style={s.fpDot('#F97316')} />
                    <span style={s.fpRowText}>Only an admin can remove it — it will <strong style={s.fpRowStrong}>not</strong> disappear automatically if the rating drops</span>
                  </div>
                </div>

                <div style={s.modalBtns}>
                  <button style={s.cancelBtn} onClick={() => setFastPayModal(null)}>Cancel</button>
                  <button
                    style={s.fpConfirmBtn('#3B82F6')}
                    onClick={handleFastPayConfirm}
                    disabled={processing === hirer.id + '_fastpay'}
                  >
                    {processing === hirer.id + '_fastpay' ? 'Granting...' : '⚡ Grant Fast Pay Badge'}
                  </button>
                </div>
              </>}

              {/* ── REVOKE ── */}
              {action === 'revoke' && <>
                <h3 style={s.fpModalTitle}>⚡ Revoke Fast Pay Badge</h3>
                <p style={s.fpModalSub}>{name}</p>

                <div style={s.fpSection}>
                  <div style={s.fpSectionTitle}>✅ When you SHOULD revoke</div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>Multiple drivers have reported <strong style={s.fpRowStrong}>late or missing payments</strong></span>
                  </div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>Payment rating has dropped significantly — current avg: <strong style={s.fpRowStrong}>{avg != null ? `${avg}★` : 'no data'}</strong></span>
                  </div>
                  <div style={s.fpRowLast}>
                    <div style={s.fpDot('#22C55E')} />
                    <span style={s.fpRowText}>The badge was manually granted in error or circumstances have changed</span>
                  </div>
                </div>

                <div style={s.fpSection}>
                  <div style={s.fpSectionTitle}>⚠️ What happens after revoking</div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#EF4444')} />
                    <span style={s.fpRowText}>Badge is <strong style={s.fpRowStrong}>immediately removed</strong> from all job cards and the hirer's profile</span>
                  </div>
                  <div style={s.fpRow}>
                    <div style={s.fpDot('#EF4444')} />
                    <span style={s.fpRowText}><strong style={s.fpRowStrong}>fastPayManualOverride is cleared</strong> — the Cloud Function takes back control and will auto-grant again if the rating climbs back to ≥ 4.5★ across 3+ reviews</span>
                  </div>
                  <div style={s.fpRowLast}>
                    <div style={s.fpDot('var(--text-secondary)')} />
                    <span style={s.fpRowText}>The hirer is <strong style={s.fpRowStrong}>not notified</strong> — badge changes are silent</span>
                  </div>
                </div>

                <div style={s.modalBtns}>
                  <button style={s.cancelBtn} onClick={() => setFastPayModal(null)}>Cancel</button>
                  <button
                    style={s.fpConfirmBtn('#EF4444')}
                    onClick={handleFastPayConfirm}
                    disabled={processing === hirer.id + '_fastpay'}
                  >
                    {processing === hirer.id + '_fastpay' ? 'Revoking...' : '⚡ Revoke Fast Pay Badge'}
                  </button>
                </div>
              </>}

            </div>
          </div>
        );
      })()}

    </div>
  );
}